import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { SITE_ORIGIN } from "../config/env";

function originUrl() {
  return SITE_ORIGIN.replace(/\/$/, "");
}

function normalizePath(path = "") {
  if (!path) return "/";
  return path.startsWith("/") ? path : `/${path}`;
}

function buildBreadcrumbJson(items) {
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }

  const base = originUrl();
  const itemListElement = items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: item.url ?? `${base}${normalizePath(item.path)}`
  }));

  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement
  });
}

function normalizeStructuredData(structuredData) {
  if (!structuredData) {
    return [];
  }
  if (Array.isArray(structuredData)) {
    return structuredData.filter(Boolean).map((item) => JSON.stringify(item));
  }
  return [JSON.stringify(structuredData)];
}

export default function Seo({
  title,
  description,
  breadcrumb,
  url,
  image,
  structuredData,
  robots = "index,follow"
}) {
  const location = useLocation();
  const breadcrumbJson = buildBreadcrumbJson(breadcrumb);
  const structuredDataJson = normalizeStructuredData(structuredData);
  
  // Generate canonical URL: use provided url, or build from current pathname
  let canonicalUrl = url;
  if (!canonicalUrl) {
    canonicalUrl = `${originUrl()}${location.pathname}`;
  }

  if (!title && !description && !breadcrumbJson && structuredDataJson.length === 0) {
    return null;
  }

  // Generate unique key to force re-render when ANY seo data changes
  const helmetKey = JSON.stringify({
    pathname: location.pathname,
    search: location.search,
    title,
    description,
    robots,
    canonicalUrl,
    breadcrumb: breadcrumb ? breadcrumb.map(b => b.name).join('|') : '',
    structuredData: structuredDataJson.join("|")
  });

  return (
    <Helmet prioritizeSeoTags key={helmetKey}>
      {title && <title>{title}</title>}
      {title && <meta property="og:title" content={title} />}
      {title && <meta name="twitter:title" content={title} />}

      {robots && <meta name="robots" content={robots} />}

      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      {canonicalUrl && <meta name="twitter:url" content={canonicalUrl} />}

      {description && <meta name="description" content={description} />}
      {description && (
        <meta property="og:description" content={description} />
      )}
      {description && (
        <meta name="twitter:description" content={description} />
      )}

      {image && <meta property="og:image" content={image} />}
      {image && <meta name="twitter:image" content={image} />}

      {breadcrumbJson && (
        <script type="application/ld+json" id="breadcrumb-jsonld">
          {breadcrumbJson}
        </script>
      )}

      {structuredDataJson.map((item, index) => (
        <script type="application/ld+json" id={`structured-data-${index}`} key={`structured-data-${index}`}>
          {item}
        </script>
      ))}
    </Helmet>
  );
}
